import { ItemCacheLockId } from 'owa-items-cache';
import { Todo } from 'owa-todo-types';
import { toTodo } from 'owa-todo-service';
import { fetchTasksForDescriptor } from './fetchTasksForDescriptor';
import { getSubtodoCompareForDescriptor } from './getCompareForDescriptor';
import { createUpsertDescriptor } from './updateDescriptorForUpsert';
import { getTodoCache } from '../todoCache';
import { TodoDescriptor } from '../schema/TodoDescriptor';
import { getDescriptorId } from '../utils/filter/getDescriptorId';
import { updateLoadState } from 'owa-todo-cache-load-state';

export async function fetchTasks(
    lockId: ItemCacheLockId,
    descriptor: TodoDescriptor,
    forceReload: boolean = false
): Promise<Todo[]> {
    const descriptorId = getDescriptorId(descriptor);
    let request;
    // We use getTodoCache().getItems here to see if this data exists in the cache for any lock
    const cacheDescriptor = { ...descriptor, Id: descriptorId };
    if (getTodoCache().getItems(cacheDescriptor) && !forceReload) {
        // The tasks for the descriptor have already been fetched,
        // so we resolve the promise with items from the cache.
        request = Promise.resolve(getTodoCache().getItems(cacheDescriptor));
    } else {
        updateLoadState(descriptorId, 'Fetching');

        // Fetch the tasks
        request = fetchTasksForDescriptor(descriptor).then(response => {
            if (!response?.errorResponse) {
                const todos = response.Value.map(toTodo);
                return todos;
            } else {
                // TODO add retry logic on failure
                throw Error(`Unable to fetch tasks ${response?.errorResponse}`);
            }
        });
    }
    return handleTodosLoaded(lockId, descriptor, request);
}

export async function handleTodosLoaded(
    lockId: ItemCacheLockId,
    descriptor: TodoDescriptor,
    promise: Promise<Todo[]>
): Promise<Todo[]> {
    const descriptorId = getDescriptorId(descriptor);
    let response: Todo[];
    try {
        response = await promise;
    } catch (error) {
        updateLoadState(descriptorId, 'Error');
        return [];
    }

    // sort subtasks based off of the descriptor
    const compareFunc = getSubtodoCompareForDescriptor(descriptor);
    compareFunc && response.forEach(task => task.Subtasks?.sort(compareFunc));

    // TODO VSO 77667: Fix incorrect todo descriptor IDs. Because descriptor id is generated before
    // this create upsert descriptor call, the descriptor id has the potential to be incorrect
    const upsertDescriptor = createUpsertDescriptor(descriptor, response);
    // Regardless of whether the items were in the cache or not, we call this upsert for the lock
    // to let the cache know this lockId, has requested data for the descriptor.
    const upsertDescriptorId = getDescriptorId(upsertDescriptor);
    const cacheDescriptor = { ...upsertDescriptor, Id: upsertDescriptorId };

    getTodoCache().upsertItemsForDescriptor(lockId, response, cacheDescriptor);

    // Notice, we update the loadState for the original descriptor and not the upsertDescriptor here.
    // We update the load state for the original descriptorId because at this point the request is already being tracked
    // in `owa-todo-cache-load-state` with the descriptorId.
    updateLoadState(descriptorId, 'Loaded');
    return response;
}
